import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, logoutAppUser, patchAppUser } from "./appAuthSlice";
import { isPendingRoundPlayable } from "../utils/bondDrawPendingExpiry";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

const adjustCoinBalance = (dispatch, getState, delta) => {
  const current = Number(getState?.()?.appAuth?.user?.COIN || 0);
  if (!Number.isFinite(current)) return;
  dispatch(patchAppUser({ COIN: Math.max(0, current + delta) }));
};

export const fetchBondDrawLanding = createAsyncThunk(
  "bondDraw/fetchLanding",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/game36/landing");
      return res.data?.pendingRound || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Draw round"));
    }
  }
);

export const fetchBondDrawPool = createAsyncThunk(
  "bondDraw/fetchPool",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/game36/pool");
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Draw pool"));
    }
  }
);

export const fetchBondDrawPlayers = createAsyncThunk(
  "bondDraw/fetchPlayers",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/game36/players", { params: { limit } });
      return res.data?.players || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Draw players"));
    }
  }
);

export const fetchBondDrawInvestors = createAsyncThunk(
  "bondDraw/fetchInvestors",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/game36/investors", { params: { limit } });
      return res.data?.investors || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Draw investors"));
    }
  }
);

export const bondDrawBuyRound = createAsyncThunk(
  "bondDraw/buy",
  async ({ selectedNumbers }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/game36/buy", { selectedNumbers });
      const round = res.data?.round || null;
      if (round?.stake) adjustCoinBalance(dispatch, getState, -Number(round.stake));
      dispatch(fetchBondDrawPool());
      dispatch(fetchBondDrawInvestors({ limit: 100 }));
      return res.data || { round };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not reserve numbers."));
    }
  }
);

export const bondDrawPlayRound = createAsyncThunk(
  "bondDraw/play",
  async ({ roundId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/game36/play", { roundId });
      const round = res.data?.round || null;
      if (round?.status === "Won") {
        const net = Number(round.playerNetPayout || round.payoutAmount || 0);
        if (net > 0) adjustCoinBalance(dispatch, getState, net);
      }
      return round;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not draw numbers."));
    }
  }
);

export const bondDrawCancelRound = createAsyncThunk(
  "bondDraw/cancel",
  async ({ roundId, stake = 0 }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/game36/cancel", { roundId });
      if (stake > 0) adjustCoinBalance(dispatch, getState, Number(stake));
      return res.data?.round || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel round."));
    }
  }
);

export const bondDrawInvest = createAsyncThunk(
  "bondDraw/invest",
  async ({ amount }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/game36/invest", { amount });
      const invested = Number(res.data?.amount || 0);
      if (invested > 0) adjustCoinBalance(dispatch, getState, -invested);
      dispatch(fetchBondDrawPool());
      dispatch(fetchBondDrawInvestors({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Invest failed."));
    }
  }
);

export const bondDrawInvestorCashout = createAsyncThunk(
  "bondDraw/investorCashout",
  async ({ amount } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/game36/investor/cashout", { amount });
      const withdrawn = Number(res.data?.amount || 0);
      if (withdrawn > 0) adjustCoinBalance(dispatch, getState, withdrawn);
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Cash out failed."));
    }
  }
);

export const bondDrawInvestorToggleActive = createAsyncThunk(
  "bondDraw/investorToggleActive",
  async ({ isActive }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/game36/investor/toggle-active", { isActive });
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not update investor status."));
    }
  }
);

const normalizePendingRound = (round) =>
  round && isPendingRoundPlayable(round) ? round : null;

const initialState = {
  pendingRound: null,
  pool: null,
  players: [],
  investors: [],
  loading: false,
  loadingPool: false,
  loadingPlayers: false,
  loadingInvestors: false,
  actionLoading: false,
  investorActionLoading: false,
  error: null,
};

const bondDrawSlice = createSlice({
  name: "bondDraw",
  initialState,
  reducers: {
    setPendingRound: (state, { payload }) => {
      state.pendingRound = payload || null;
    },
    clearPendingRound: (state) => {
      state.pendingRound = null;
    },
    resetBondDrawState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBondDrawLanding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBondDrawLanding.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.pendingRound = normalizePendingRound(payload);
      })
      .addCase(fetchBondDrawLanding.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || "Failed to load Bond Draw round";
      })
      .addCase(fetchBondDrawPool.pending, (state) => {
        state.loadingPool = true;
      })
      .addCase(fetchBondDrawPool.fulfilled, (state, { payload }) => {
        state.loadingPool = false;
        state.pool = payload || null;
      })
      .addCase(fetchBondDrawPool.rejected, (state) => {
        state.loadingPool = false;
      })
      .addCase(fetchBondDrawPlayers.pending, (state) => {
        state.loadingPlayers = true;
      })
      .addCase(fetchBondDrawPlayers.fulfilled, (state, { payload }) => {
        state.loadingPlayers = false;
        state.players = payload || [];
      })
      .addCase(fetchBondDrawPlayers.rejected, (state) => {
        state.loadingPlayers = false;
        state.players = [];
      })
      .addCase(fetchBondDrawInvestors.pending, (state) => {
        state.loadingInvestors = true;
      })
      .addCase(fetchBondDrawInvestors.fulfilled, (state, { payload }) => {
        state.loadingInvestors = false;
        state.investors = payload || [];
      })
      .addCase(fetchBondDrawInvestors.rejected, (state) => {
        state.loadingInvestors = false;
        state.investors = [];
      })
      .addCase(bondDrawBuyRound.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(bondDrawBuyRound.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        const round = payload?.round || payload;
        state.pendingRound = normalizePendingRound(round);
      })
      .addCase(bondDrawBuyRound.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload || "Could not reserve numbers";
      })
      .addCase(bondDrawPlayRound.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(bondDrawPlayRound.fulfilled, (state) => {
        state.actionLoading = false;
        state.pendingRound = null;
      })
      .addCase(bondDrawPlayRound.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload || "Could not draw numbers";
      })
      .addCase(bondDrawCancelRound.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(bondDrawCancelRound.fulfilled, (state) => {
        state.actionLoading = false;
        state.pendingRound = null;
      })
      .addCase(bondDrawCancelRound.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload || "Could not cancel round";
      })
      .addCase(bondDrawInvest.pending, (state) => {
        state.investorActionLoading = true;
        state.error = null;
      })
      .addCase(bondDrawInvest.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDrawInvest.rejected, (state, { payload }) => {
        state.investorActionLoading = false;
        state.error = payload || "Invest failed";
      })
      .addCase(bondDrawInvestorCashout.pending, (state) => {
        state.investorActionLoading = true;
        state.error = null;
      })
      .addCase(bondDrawInvestorCashout.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDrawInvestorCashout.rejected, (state, { payload }) => {
        state.investorActionLoading = false;
        state.error = payload || "Cash out failed";
      })
      .addCase(bondDrawInvestorToggleActive.pending, (state) => {
        state.investorActionLoading = true;
        state.error = null;
      })
      .addCase(bondDrawInvestorToggleActive.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondDrawInvestorToggleActive.rejected, (state, { payload }) => {
        state.investorActionLoading = false;
        state.error = payload || "Could not update investor status";
      })
      .addCase(logoutAppUser.fulfilled, () => initialState);
  },
});

export const { setPendingRound, clearPendingRound, resetBondDrawState } = bondDrawSlice.actions;
export default bondDrawSlice.reducer;

export const selectBondDrawPendingRound = (state) => state.bondDraw?.pendingRound || null;
export const selectBondDrawPool = (state) => state.bondDraw?.pool || null;
export const selectBondDrawPlayers = (state) => state.bondDraw?.players || [];
export const selectBondDrawInvestors = (state) => state.bondDraw?.investors || [];
export const selectBondDrawLoading = (state) => !!state.bondDraw?.loading;
export const selectBondDrawLoadingPool = (state) => !!state.bondDraw?.loadingPool;
export const selectBondDrawLoadingPlayers = (state) => !!state.bondDraw?.loadingPlayers;
export const selectBondDrawLoadingInvestors = (state) => !!state.bondDraw?.loadingInvestors;
export const selectBondDrawActionLoading = (state) => !!state.bondDraw?.actionLoading;
export const selectBondDrawInvestorActionLoading = (state) => !!state.bondDraw?.investorActionLoading;
export const selectBondDrawError = (state) => state.bondDraw?.error || null;
